/**
 * iter87.262/263 — Playoff Series Rivalries Intel (frontend tile).
 *
 * Opened via the TodoChips "Series Intel" chip in the In-Season row for
 * MLB/NBA Playoffs. Two independent sections:
 *  - "Science says…" — deterministic factor blend (season W/L, home/away,
 *    last-10, MLB starting-pitcher ERA), ONE winner+games pick per active
 *    series, no ranking/EV shown. Locks (🔒) the moment that series'
 *    Game 1 is recorded as played — frozen from then on.
 *  - "Bot says…" — LLM Sharp/Trap picks, one team + game-count each.
 *
 * iter87.263 (user-reported) — team-color swatches are back here (the
 * user liked them specifically in this modal) after iter87.262 had
 * removed them app-wide from every playoff pick surface. They stay
 * removed everywhere else (Series Predictor, Standings/bracket tile).
 */
import { Bot, TrendingUp, AlertTriangle, X, Zap, FlaskConical, Lock } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

function SeriesRow({ s, sport }) {
    return (
        <li data-testid={`playoff-intel-series-${s.series_id}`} className="py-2 border-b border-zinc-900 last:border-b-0">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                {s.round_label}{s.best_of > 1 ? (sport === "ucl" ? " · 2-Leg Tie" : ` · Best of ${s.best_of}`) : ""}
            </p>
            <p className="text-sm font-bold text-white mt-1 flex items-center gap-1.5 flex-wrap">
                {/* iter87.273 (user-reported) — swatch + name + seed
                    must move together when the row wraps. They used
                    to be separate flex items in this flex-wrap row,
                    so the wrap point could land BETWEEN the swatch and
                    its own team's name (swatch stranded on line 1,
                    name alone on line 2). Grouping them into one
                    inline-flex span makes the whole trio wrap as a
                    single atomic unit. */}
                <span className="inline-flex items-center gap-1.5">
                    <TeamSwatch name={s.team_a?.name} sport={sport} size="w-5 h-5 text-[8px]" />
                    {s.team_a?.name} <span className="text-zinc-500 font-normal">(seed {s.team_a?.seed})</span>
                </span>
                <span className="text-zinc-600">vs</span>
                <span className="inline-flex items-center gap-1.5">
                    <TeamSwatch name={s.team_b?.name} sport={sport} size="w-5 h-5 text-[8px]" />
                    {s.team_b?.name} <span className="text-zinc-500 font-normal">(seed {s.team_b?.seed})</span>
                </span>
            </p>
            <p className="text-[10px] text-zinc-500 mt-0.5">
                {s.best_of > 1 && <>{sport === "ucl" ? "Leg score" : "Series score"} {s.series_score}</>}
                {s.team_a?.season_record?.wins != null && (
                    <> {s.best_of > 1 ? "· " : ""}{s.team_a.name} {s.team_a.season_record.wins}-{s.team_a.season_record.losses}</>
                )}
                {s.team_b?.season_record?.wins != null && (
                    <> · {s.team_b.name} {s.team_b.season_record.wins}-{s.team_b.season_record.losses}</>
                )}
            </p>
        </li>
    );
}

const NWSL_DECIDED_BY_LABEL = { regulation: "Regulation", extra_time: "Extra Time", penalties: "Penalties" };

function ScienceRow({ s, sport }) {
    const sci = s.science;
    if (!sci) {
        return (
            <li data-testid={`playoff-science-row-${s.series_id}`} className="py-2 border-b border-zinc-900 last:border-b-0">
                <p className="text-[11px] text-zinc-500">
                    {s.team_a?.name} vs {s.team_b?.name} — <span className="italic">
                        {(sport === "nfl" || sport === "cfb" || (sport === "mls" && s.best_of === 1)) ? "odds not posted yet" : "not enough data yet"}
                    </span>
                </p>
            </li>
        );
    }
    if (sport === "nwsl") {
        return (
            <li data-testid={`playoff-science-row-${s.series_id}`} className="py-2 border-b border-zinc-900 last:border-b-0">
                <p className="text-[11px] text-zinc-400 flex items-center gap-1.5 min-w-0 mb-1.5">
                    <TeamSwatch name={s.team_a?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                    <span className="truncate">{s.team_a?.name}</span>
                    <span className="text-zinc-600 shrink-0">vs</span>
                    <TeamSwatch name={s.team_b?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                    <span className="truncate">{s.team_b?.name}</span>
                    {sci.locked && <Lock className="w-2.5 h-2.5 text-zinc-500 shrink-0 ml-auto" data-testid={`playoff-science-locked-${s.series_id}`} />}
                </p>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-zinc-600">Winner</p>
                        <p className="text-xs font-bold text-emerald-400 leading-tight line-clamp-2">{sci.winner_pick_name}</p>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-zinc-600">Decided by</p>
                        <p className="text-xs font-bold text-cyber">{NWSL_DECIDED_BY_LABEL[sci.decided_by_pick] || sci.decided_by_pick}</p>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-zinc-600">Total goals</p>
                        <p className="text-xs font-bold text-heat capitalize">{sci.total_goals_pick} {sci.goals_line}</p>
                    </div>
                </div>
            </li>
        );
    }
    // iter87.465.24 (user-requested — "Match Predictor... as it is
    // with Asian handicap and over/under total goals - like NFL and
    // CFB") — MLS's single-game Conf Semis+ rounds (best_of === 1)
    // reuse the exact same spread+total UI as NFL/CFB (The Odds API's
    // `spreads` market IS soccer's Asian handicap; `totals` is goals).
    // MLS's best-of-3 Round One (best_of > 1) falls through to the
    // generic predicted-winner pill below instead — no odds synced
    // for that round, same as MLB/NBA.
    if (sport === "nfl" || sport === "cfb" || (sport === "mls" && s.best_of === 1)) {
        return (
            <li data-testid={`playoff-science-row-${s.series_id}`} className="py-2 border-b border-zinc-900 last:border-b-0">
                <p className="text-[11px] text-zinc-400 flex items-center gap-1.5 min-w-0 mb-1.5">
                    <TeamSwatch name={s.team_a?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                    <span className="truncate">{s.team_a?.name}</span>
                    <span className="text-zinc-600 shrink-0">vs</span>
                    <TeamSwatch name={s.team_b?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                    <span className="truncate">{s.team_b?.name}</span>
                    {sci.locked && <Lock className="w-2.5 h-2.5 text-zinc-500 shrink-0 ml-auto" data-testid={`playoff-science-locked-${s.series_id}`} />}
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-zinc-600">Favorite</p>
                        <p className="text-xs font-bold text-emerald-400 leading-tight line-clamp-2">{sci.favorite_name} {sci.spread_line}</p>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider font-bold text-zinc-600">Total</p>
                        {sci.total_line != null ? (
                            <p className="text-xs font-bold text-heat">O/U {sci.total_line}</p>
                        ) : (
                            <p className="text-xs font-bold text-zinc-500 italic">odds not posted yet</p>
                        )}
                    </div>
                </div>
            </li>
        );
    }
    return (
        <li data-testid={`playoff-science-row-${s.series_id}`} className="py-2 border-b border-zinc-900 last:border-b-0 flex items-center justify-between gap-2">
            <p className="text-[11px] text-zinc-400 flex items-center gap-1.5 min-w-0">
                <TeamSwatch name={s.team_a?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                <span className="truncate">{s.team_a?.name}</span>
                <span className="text-zinc-600 shrink-0">vs</span>
                <TeamSwatch name={s.team_b?.name} sport={sport} size="w-4 h-4 text-[7px]" />
                <span className="truncate">{s.team_b?.name}</span>
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
                {sci.locked && <Lock className="w-2.5 h-2.5 text-zinc-500" data-testid={`playoff-science-locked-${s.series_id}`} />}
                <span className="pill !text-[10px] !py-0.5 border border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                    {sci.predicted_winner_name} in {sci.predicted_games}
                </span>
            </div>
        </li>
    );
}

function PickCard({ p, tone, icon: Icon, label, testIdKind }) {
    return (
        <div data-testid={`playoff-intel-${testIdKind}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-3 h-3 ${tone}`} />
                <p className={`text-[10px] uppercase tracking-wider font-bold ${tone}`}>{label}</p>
            </div>
            <div className="brutal-card p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-white text-sm">{p.team}</span>
                    {p.series_pick && (
                        <span
                            data-testid={`playoff-intel-${testIdKind}-game-count`}
                            className={`pill !text-[10px] !py-0.5 border ${tone === "text-cyber" ? "border-cyber/50 bg-cyber/10" : "border-heat/50 bg-heat/10"} ${tone}`}
                        >
                            {p.series_pick}
                        </span>
                    )}
                    {p.scoreline && (
                        <span data-testid={`playoff-intel-${testIdKind}-scoreline`} className="font-mono font-bold text-white text-sm">
                            ({p.scoreline})
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">{p.why}</p>
            </div>
        </div>
    );
}

export default function PlayoffSeriesIntelModal({ open = true, intel, onClose, scope }) {
    const series = intel?.series || [];
    const sport = intel?.sport;
    const sharps = (intel?.sharps || []).slice(0, 1);
    const trapPicks = (intel?.trap_picks || []).slice(0, 1);
    const hasData = series.length > 0;
    // iter87.465.26 (user-requested — "there should be 2 intel
    // tiles") — MLS now fetches two INDEPENDENTLY-scoped docs (see
    // `usePlayoffSeriesIntel.js`), so this modal is opened twice, once
    // per scope, each with its own fixed title passed in explicitly —
    // no more inferring from `intel.round_key` (which used to rotate
    // through wildcard/round_one/conf_semis/etc. off a single shared
    // tile). NFL/CFB/MLB/NBA/NWSL pass no `scope`.
    // iter87.465.29 (user-requested — "call the nfl cfb nwsl rivalry
    // intel playoff intel to match the MLS naming convention") —
    // single-elimination sports (NFL/CFB/NWSL) now say "Playoff
    // Intel"; MLB/NBA stay "Rivalry Intel" (series-based, untouched).
    const SINGLE_ELIM_SPORTS = new Set(["nwsl", "nfl", "cfb"]);
    const title = scope === "series" ? "Series Intel"
        : scope === "knockout" ? "Playoff Intel"
        : scope === "two_leg" ? "Two-Legged Intel"
        : scope === "final" ? "Champions Intel"
        : SINGLE_ELIM_SPORTS.has(sport) ? "Playoff Intel"
        : "Rivalry Intel";

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="playoff-series-intel-modal"
            backdropClassName="items-end sm:items-center justify-center sm:p-4"
            panelClassName="brutal-card w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto bg-ink border-2 border-volt shadow-2xl"
        >
            <div className="sticky top-0 z-10 bg-ink border-b-2 border-zinc-800 px-4 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Zap className="w-4 h-4 text-volt shrink-0" />
                    <h2 className="font-display font-extrabold text-base uppercase tracking-wide truncate" data-testid="playoff-intel-title">
                        {title}
                    </h2>
                    {intel?.round_label && (
                        <span
                            data-testid="playoff-intel-round-badge"
                            className="pill text-[10px] font-black uppercase tracking-wider border-volt/50 text-volt bg-volt/10 shrink-0"
                        >
                            {intel.round_label}
                        </span>
                    )}
                </div>
                <button
                    data-testid="playoff-intel-close-btn"
                    onClick={onClose}
                    className="p-1.5 text-zinc-500 hover:text-white shrink-0"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="p-4 space-y-4">
                {intel?.projected && hasData && (
                    <div
                        data-testid="playoff-intel-projected-banner"
                        className="bg-amber-500/10 border-2 border-amber-500 text-amber-400 p-3 rounded-lg text-sm flex items-start gap-2 text-left"
                    >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <p className="leading-relaxed">
                            <span className="font-bold uppercase tracking-tight">Provisional:</span> seeding isn't final yet — these are today's standings snapshot, matchups can still shift before real games start.
                        </p>
                    </div>
                )}

                {!hasData && (
                    <div className="brutal-card p-4 text-center text-xs text-zinc-400 leading-relaxed" data-testid="playoff-intel-empty-state">
                        {scope === "final"
                            ? "The Final hasn't been drawn yet — Champions Intel picks up once the two finalists are set."
                            : scope === "two_leg"
                                ? "No active ties right now — Two-Legged Intel picks up as soon as the next round's matchups are set."
                                : `No active playoff series right now — ${title} picks up as soon as the next round's matchups are set.`}
                    </div>
                )}

                {hasData && (
                    <>
                        <section data-testid="playoff-intel-series-list">
                            <p className="text-[11px] uppercase tracking-widest font-bold text-white mb-1">
                                This round
                            </p>
                            <ul className="brutal-card divide-y divide-zinc-900 px-3 py-1">
                                {series.map((s) => <SeriesRow key={s.series_id} s={s} sport={sport} />)}
                            </ul>
                        </section>

                        {(
                            <section data-testid="playoff-intel-card-science" className="space-y-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <FlaskConical className="w-3.5 h-3.5 text-emerald-400" />
                                    <p className="text-[11px] uppercase tracking-widest font-bold text-white">
                                        Science says…
                                    </p>
                                </div>
                                <ul className="brutal-card divide-y divide-zinc-900 px-3 py-1">
                                    {series.map((s) => <ScienceRow key={s.series_id} s={s} sport={sport} />)}
                                </ul>
                                <p className="text-[9px] text-zinc-500 leading-snug">
                                    {sport === "nwsl" ? (
                                        "No real betting line exists for NWSL — Winner, Decided-By, and Total Goals are our own projection from recent form. Locks once kickoff passes."
                                    ) : (sport === "nfl" || sport === "cfb" || (sport === "mls" && series[0]?.best_of === 1)) ? (
                                        "Real spread + total odds from the sportsbook market — not a model guess. Locks once kickoff passes."
                                    ) : (
                                        <>Season W/L, home/away split, last-10 record{sport === "mlb" ? ", starting pitcher ERA" : ""} — blended, no odds factored in yet. Locks per series once Game 1 is played.</>
                                    )}
                                </p>
                            </section>
                        )}

                        {intel.llm_paused && intel.paused_copy && (
                            <section
                                data-testid="playoff-intel-paused-banner"
                                className="brutal-card-light p-3 border-amber-500/40 bg-amber-500/10 space-y-1"
                            >
                                <span className="pill bg-amber-500/20 border-amber-500/60 text-amber-300 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest">
                                    AI paused
                                </span>
                                <p className="text-[12px] font-bold text-white">{intel.paused_copy.headline}</p>
                                <p className="text-[11px] text-zinc-400 leading-snug">{intel.paused_copy.body}</p>
                            </section>
                        )}

                        {!intel.llm_paused && (sharps.length > 0 || trapPicks.length > 0) && (
                            <section data-testid="playoff-intel-card-bot" className="space-y-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <Bot className="w-3.5 h-3.5 text-cyber" />
                                    <p className="text-[11px] uppercase tracking-widest font-bold text-white">
                                        Bot says…
                                    </p>
                                </div>
                                {sharps.length > 0 && (
                                    <PickCard p={sharps[0]} tone="text-cyber" icon={TrendingUp} label="Sharp Pick" testIdKind="sharp" />
                                )}
                                {trapPicks.length > 0 && (
                                    <PickCard p={trapPicks[0]} tone="text-heat" icon={AlertTriangle} label="Trap Pick" testIdKind="trap" />
                                )}
                            </section>
                        )}

                        {!intel.llm_paused && sharps.length === 0 && trapPicks.length === 0 && (
                            <section data-testid="playoff-intel-card-bot-unavailable" className="space-y-1.5">
                                <div className="flex items-center gap-2 mb-1">
                                    <Bot className="w-3.5 h-3.5 text-zinc-500" />
                                    <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">
                                        Bot says…
                                    </p>
                                </div>
                                <p className="text-[10px] text-zinc-500 leading-snug">
                                    The bot's taking a breather — no Sharp Pick or Trap Pick this round. Check back in a bit.
                                </p>
                            </section>
                        )}

                        <p className="text-[9px] text-zinc-600 text-center pt-1 border-t border-zinc-900">
                            {intel.llm_source && (
                                <>Bot says commentary via {intel.llm_source === "anthropic" ? "Claude" : "Claude (Emergent)"} · </>
                            )}
                            Refreshed {intel.generated_at ? new Date(intel.generated_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "recently"}
                        </p>
                    </>
                )}
            </div>
        </BentoModal>
    );
}
